'use client';

/**
 * OpsPanel — shared wrapper for cards on the Analytics tabs.
 *
 * Gives every analytics card a consistent avionics frame: badge banner
 * on the top-left (e.g. "5-Week Volume"), optional system code on the
 * top-right, hairline top accent, and corner brackets on the bottom
 * corners. Matches the SITREP page visual family.
 *
 * Use it instead of the old `bg-slate-800/80 rounded-xl p-4 border ...`
 * pattern that was scattered across TrainingTab / BodyTab / RecoveryTab
 * / IntelligenceTab.
 */

import type { ReactNode } from 'react';

interface Props {
  /** Top-left badge label, ALL CAPS mono. */
  badge: string;
  /** Optional 3-letter system code on the top-right. */
  code?: string;
  /** Optional right-aligned stat callout (e.g. current value + delta). */
  callout?: ReactNode;
  /** Optional accent color — `amber` is the default. */
  accent?: 'amber' | 'cyan' | 'emerald' | 'red' | 'slate';
  /** Disable corner brackets for visual breathing room when stacking many. */
  noBrackets?: boolean;
  /**
   * Density preset:
   *   - normal  (default): roomy — best when the panel has rich content
   *   - compact: tighter vertical padding, smaller header gap
   */
  density?: 'normal' | 'compact';
  children: ReactNode;
  className?: string;
}

/**
 * `badgeBg` is written out in full rather than derived from `badgeTw` at
 * runtime. It used to be `badgeTw.replace('text', 'bg')`, which produces a
 * class string Tailwind's scanner NEVER sees — so the tick only rendered
 * because those exact `bg-*` classes happened to appear statically somewhere
 * else in the codebase. That made it silently transparent the moment an
 * unrelated file stopped using them (this became a live risk when ~3,600 lines
 * of dead metrics components were deleted). Literal keys are purge-safe.
 */
const ACCENT_MAP = {
  amber: { line: 'via-amber-500/60', brkt: 'border-amber-500/40', badgeTw: 'text-amber-400', badgeBg: 'bg-amber-400' },
  cyan: { line: 'via-hud-cyan/60', brkt: 'border-hud-cyan/40', badgeTw: 'text-hud-cyan', badgeBg: 'bg-hud-cyan' },
  emerald: { line: 'via-emerald-500/60', brkt: 'border-emerald-500/40', badgeTw: 'text-emerald-400', badgeBg: 'bg-emerald-400' },
  red: { line: 'via-red-500/60', brkt: 'border-red-500/40', badgeTw: 'text-red-400', badgeBg: 'bg-red-400' },
  slate: { line: 'via-slate-500/60', brkt: 'border-slate-500/40', badgeTw: 'text-slate-400', badgeBg: 'bg-slate-400' },
};

export default function OpsPanel({
  badge,
  code,
  callout,
  accent = 'amber',
  noBrackets = false,
  density = 'normal',
  children,
  className = '',
}: Props) {
  const a = ACCENT_MAP[accent];
  const dense = density === 'compact';
  return (
    <section className={`relative overflow-hidden rounded-xl border border-slate-800/80 bg-hud-ink/80 ${className}`}>
      {/* Hairline top accent */}
      <div aria-hidden className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent ${a.line} to-transparent`} />
      {/* Corner brackets — bottom only so they don't fight the top accent */}
      {!noBrackets && (
        <>
          <span aria-hidden className={`pointer-events-none absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l ${a.brkt}`} />
          <span aria-hidden className={`pointer-events-none absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r ${a.brkt}`} />
        </>
      )}

      <div className={dense ? 'px-3 pt-2 pb-2.5' : 'px-4 pt-2.5 pb-3'}>
        {/* Header strip */}
        <div className={`flex items-center justify-between gap-2 ${dense ? 'mb-1.5' : 'mb-2.5'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-0.5 h-3 ${a.badgeBg}`} aria-hidden />
            <span className={`font-mono text-[10px] uppercase tracking-[0.22em] font-bold truncate ${a.badgeTw}`}>
              {badge}
            </span>
            {code && (
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600 hidden sm:inline truncate">
                {code}
              </span>
            )}
          </div>
          {callout && (
            <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-300 tabular-nums shrink-0">
              {callout}
            </div>
          )}
        </div>

        {children}
      </div>
    </section>
  );
}
